import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useData } from '@/contexts/DataContext';
import { useNavigate } from 'react-router-dom';
import { STAGE_LABELS, Stage } from '@/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Trash2, Filter, UserPlus, RotateCcw, Loader2, Wifi, WifiOff } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { ANALYSIS_THRESHOLD } from '@/data/mockData';

export default function PitchesPage() {
  const { user, allUsers } = useAuth();
  const { pitches, deletePitch, isDeletable, assignPitch, restorePitch, permanentlyDeletePitch, refreshPitches, isSupabaseConnected } = useData();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [pitchToDelete, setPitchToDelete] = useState<string | null>(null);
  const [pitchToPermanentDelete, setPitchToPermanentDelete] = useState<string | null>(null);
  const [stageFilter, setStageFilter] = useState<Stage | 'all'>('all');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [pitchToAssign, setPitchToAssign] = useState<string | null>(null);
  const [selectedAnalyst, setSelectedAnalyst] = useState('');

  useEffect(() => {
    refreshPitches();
  }, []);

  if (!user) {
    return null;
  }

  const isAdmin = user.role === 'senior_analyst';
  const isAnalyst = user.role === 'analyst' || user.role === 'senior_analyst';
  const isBusinessAnalyst = user.role === 'analyst' && user.analyst_type === 'business';
  const isProblemBankAnalyst = user.role === 'analyst' && user.analyst_type === 'problem_bank';
  
  const analysts = allUsers.filter(u => u.role === 'analyst' || u.role === 'senior_analyst');

  const canBeAssigned = (pitch: any) => {
    if (!pitch.analysisScores) return false;
    return pitch.analysisScores.overall >= ANALYSIS_THRESHOLD && pitch.stage === 'awaiting_assignment';
  };

  const visible = pitches.filter(p => {
    if (user.role === 'founder' && p.founderId !== user.id) return false;
    
    if (user.role === 'senior_analyst') {
      if (p.deletedAt) return false;
      if (p.stage !== 'awaiting_assignment') return false;
      if (!p.analysisScores || p.analysisScores.overall < ANALYSIS_THRESHOLD) return false;
      return true;
    }
    
    if (isBusinessAnalyst) {
      if (p.deletedAt) return false;
      return true;
    }
    
    if (isProblemBankAnalyst && p.assignedAnalystId !== user.id) return false;
    
    if (user.role === 'analyst' && !isBusinessAnalyst && p.assignedAnalystId !== user.id) return false;
    
    if (p.deletedAt && user.role !== 'founder') return false;
    
    if (stageFilter !== 'all' && p.stage !== stageFilter) return false;
    
    return true;
  }).sort((a, b) => {
    if (a.deletedAt && !b.deletedAt) return 1;
    if (!a.deletedAt && b.deletedAt) return -1;
    return 0;
  });

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getAssignedAnalystName = (analystId: string) => {
    const analyst = allUsers.find(u => u.id === analystId);
    return analyst?.name || 'Unknown';
  };

  const handleDeleteClick = async (pitchId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!user) return;
    setIsProcessing(true);
    try {
      await deletePitch(pitchId, user.id);
      toast({
        title: "Pitch deleted",
        description: "Your pitch has been moved to deleted items.",
      });
      setPitchToDelete(null);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete pitch. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRestoreClick = async (pitchId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!user) return;
    setIsProcessing(true);
    try {
      const success = await restorePitch(pitchId, user.id);
      if (success) {
        toast({
          title: "Pitch Restored",
          description: "The pitch has been restored to your dashboard.",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to restore pitch.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePermanentDeleteClick = async (pitchId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!user) return;
    setIsProcessing(true);
    try {
      const success = await permanentlyDeletePitch(pitchId, user.id);
      if (success) {
        toast({
          title: "Pitch Permanently Deleted",
          description: "The pitch has been completely removed from the system.",
        });
        setPitchToPermanentDelete(null);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to permanently delete pitch.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAssignClick = (pitchId: string, e: React.MouseEvent, pitch: any) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (!canBeAssigned(pitch)) {
      toast({
        title: "Cannot Assign",
        description: "This pitch must pass AI analysis (score 70+) before it can be assigned to an analyst.",
        variant: "destructive",
      });
      return;
    }
    
    setPitchToAssign(pitchId);
    setAssignDialogOpen(true);
  };

  const handleAssignPitch = async () => {
    if (pitchToAssign && selectedAnalyst && user) {
      setIsProcessing(true);
      try {
        await assignPitch(pitchToAssign, selectedAnalyst, user.id);
        setAssignDialogOpen(false);
        setPitchToAssign(null);
        setSelectedAnalyst('');
        toast({
          title: "Pitch assigned",
          description: "The pitch has been assigned to the analyst.",
        });
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to assign pitch.",
          variant: "destructive",
        });
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refreshPitches();
    setIsRefreshing(false);
    toast({ title: "Refreshed", description: "Pitches updated." });
  };

  const handlePitchClick = (pitch: any, e?: React.MouseEvent) => {
    if (e?.target instanceof HTMLElement) {
      if (e.target.closest('button')) {
        return;
      }
    }
    
    if (pitch.deletedAt) return;
    
    if (user.role === 'founder') {
      navigate(`/pitch/${pitch.id}`);
    } else {
      navigate(`/analyst/pitch/${pitch.id}`);
    }
  };

  const stageOptions: (Stage | 'all')[] = ['all', ...Object.keys(STAGE_LABELS) as Stage[]];

  return (
    <div className="space-y-4 max-w-5xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Pitches</h1>
          <div className="flex items-center gap-2 mt-1">
            {isSupabaseConnected ? (
              <div className="flex items-center gap-1">
                <Wifi className="h-3 w-3 text-green-500" />
                <span className="text-xs text-green-600">Live</span>
              </div>
            ) : (
              <div className="flex items-center gap-1">
                <WifiOff className="h-3 w-3 text-red-500" />
                <span className="text-xs text-red-600">Offline (using local)</span>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isRefreshing}>
            {isRefreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Refresh'}
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Filter className="h-4 w-4 mr-2" />
                {stageFilter === 'all' ? 'All Stages' : STAGE_LABELS[stageFilter]}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {stageOptions.map(stage => (
                <DropdownMenuCheckboxItem
                  key={stage}
                  checked={stageFilter === stage}
                  onCheckedChange={() => setStageFilter(stage)}
                >
                  {stage === 'all' ? 'All Stages' : STAGE_LABELS[stage]}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {user.role === 'founder' && (
            <Button size="sm" onClick={() => navigate('/pitch/new/basics')}>
              New Pitch
            </Button>
          )}
        </div>
      </div>

      {visible.length === 0 ? (
        <div className="text-center py-12 border rounded-lg bg-muted/10">
          <p className="text-muted-foreground">No pitches to display.</p>
          {user.role === 'founder' && (
            <Button variant="outline" className="mt-4" onClick={() => navigate('/pitch/new/basics')}>
              Create Your First Pitch
            </Button>
          )}
          {user.role === 'senior_analyst' && (
            <p className="text-xs text-muted-foreground mt-2">
              No pitches awaiting assignment. Pitches appear here after passing AI analysis (score 70+).
            </p>
          )}
          {isBusinessAnalyst && (
            <p className="text-xs text-muted-foreground mt-2">
              Business Analysts can view all pitches in the system.
            </p>
          )}
        </div>
      ) : (
        <div className="border rounded-md divide-y">
          {visible.map(p => {
            const founder = allUsers.find(u => u.id === p.founderId);
            const isOwner = user.role === 'founder' && p.founderId === user.id;
            const assignable = canBeAssigned(p);
            
            return (
              <div
                key={p.id}
                className={`flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50 transition-colors ${
                  p.deletedAt ? 'opacity-50 bg-muted/20' : ''
                }`}
                onClick={(e) => handlePitchClick(p, e)}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-base">{p.companyName}</p>
                    {p.deletedAt && (
                      <Badge variant="outline" className="text-muted-foreground">
                        Deleted
                      </Badge>
                    )}
                    {p.analysisScores && p.analysisScores.overall >= ANALYSIS_THRESHOLD && !p.deletedAt && (
                      <Badge className="bg-green-100 text-green-700 border-green-200">
                        Passed Analysis
                      </Badge>
                    )}
                    {p.analysisScores && p.analysisScores.overall < ANALYSIS_THRESHOLD && !p.deletedAt && (
                      <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">
                        Needs Improvement
                      </Badge>
                    )}
                    {p.stage === 'awaiting_assignment' && !p.deletedAt && (
                      <Badge className="bg-blue-100 text-blue-700 border-blue-200">
                        Ready for Assignment
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground truncate">{p.tagline}</p>
                  
                  {isAdmin && founder && (
                    <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                      <span>Founder: {founder.name}</span>
                    </div>
                  )}
                  
                  {p.analysisScores && (
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs font-medium">Score:</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        p.analysisScores.overall >= 70 ? 'bg-green-100 text-green-700' :
                        p.analysisScores.overall >= 50 ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {p.analysisScores.overall}/100
                      </span>
                    </div>
                  )}
                  
                  {p.assignedAnalystId && (
                    <div className="text-xs text-muted-foreground mt-1">
                      Assigned to: {getAssignedAnalystName(p.assignedAnalystId)}
                    </div>
                  )}
                </div>
                
                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <Badge variant="outline" className="text-xs">
                    {STAGE_LABELS[p.stage]}
                  </Badge>
                  
                  {isAdmin && !p.assignedAnalystId && !p.deletedAt && (
                    <Button
                      variant="default"
                      size="sm"
                      className={assignable ? "bg-green-600 hover:bg-green-700" : "bg-gray-400 cursor-not-allowed"}
                      onClick={(e) => handleAssignClick(p.id, e, p)}
                      disabled={isProcessing || !assignable}
                      title={!assignable ? "Pitch must pass AI analysis (score 70+) before assignment" : "Assign to analyst"}
                    >
                      <UserPlus className="h-3 w-3 mr-1" />
                      Assign
                    </Button>
                  )}
                  
                  {isOwner && p.deletedAt && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-green-500 text-green-600 hover:bg-green-50"
                        onClick={(e) => handleRestoreClick(p.id, e)}
                        disabled={isProcessing}
                      >
                        <RotateCcw className="h-3 w-3 mr-1" />
                        Restore
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-red-500 text-red-600 hover:bg-red-50"
                        onClick={(e) => handlePermanentDeleteClick(p.id, e)}
                        disabled={isProcessing}
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Delete Forever
                      </Button>
                    </>
                  )}
                  
                  {isOwner && !p.deletedAt && isDeletable(p, user.id) && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="opacity-50 hover:opacity-100"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setPitchToDelete(p.id);
                      }}
                      disabled={isProcessing}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <AlertDialog open={!!pitchToDelete} onOpenChange={() => setPitchToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this pitch?</AlertDialogTitle>
            <AlertDialogDescription>
              This action moves the pitch to deleted items. You can restore it later or permanently delete it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={(e) => pitchToDelete && handleDeleteClick(pitchToDelete, e as any)}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!pitchToPermanentDelete} onOpenChange={() => setPitchToPermanentDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently Delete This Pitch?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The pitch will be completely removed from the system and cannot be recovered.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={(e) => pitchToPermanentDelete && handlePermanentDeleteClick(pitchToPermanentDelete, e as any)}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete Forever
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Pitch to Analyst</DialogTitle>
            <DialogDescription>
              Select an analyst to review this pitch. The pitch has passed AI analysis.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Select Analyst</label>
              <Select value={selectedAnalyst} onValueChange={setSelectedAnalyst}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose an analyst" />
                </SelectTrigger>
                <SelectContent>
                  {analysts.map(analyst => (
                    <SelectItem key={analyst.id} value={analyst.id}>
                      <div className="flex items-center gap-2">
                        <Avatar className="h-6 w-6">
                          <AvatarFallback className="text-xs">
                            {getInitials(analyst.name)}
                          </AvatarFallback>
                        </Avatar>
                        <span>{analyst.name}</span>
                        <Badge variant="outline" className="ml-2 text-xs">
                          {analyst.role === 'senior_analyst' ? 'Senior' : 'Analyst'}
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAssignPitch} disabled={!selectedAnalyst || isProcessing}>
              {isProcessing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Assign Pitch
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}